import { LoginModal } from '@/components/auth/LoginModal'
import { SessionExpiredModal } from '@/components/errors/SessionExpiredModal'
import { useAuthModalStore } from '@/stores/authModalStore'
import { useAuthStore } from '@/stores/authStore'
import { useFeatureFlagsStore } from '@/stores/featureFlagsStore'
import { useSessionStore } from '@/stores/sessionStore'
import { useEffect } from 'react'
import { Outlet } from 'react-router'
import { Footer } from './Footer'
import { Navbar } from './Navbar'

/**
 * Application shell — Variant C `.ppt-page` 루트.
 *
 * 목업 원본: `docs/review/mockups/20260512_165854_dataset-list-C.html:128-238` 기준.
 * 실제 DOM 계층 (형제 3개):
 *
 *   .ppt-page
 *     .canvas          (main content area — 페이지 본문 스크롤 컨테이너)
 *       .gnb           ← canvas 내부 첫 자식 (NOT sibling of canvas)
 *       <Outlet />     ← 각 페이지가 canvas 잔여 영역에 렌더
 *     footer.c-footer  (40px 고정 푸터)
 *
 * Step 2 범위:
 *  - `.canvas` 컨테이너를 AppShell 이 책임 (13 feature 페이지 수정 없이 적용)
 *  - `.gnb` 가 `.canvas` 내부에 위치하는 목업 구조를 그대로 반영
 */
export function AppShell() {
  const fetchMe = useAuthStore((s) => s.fetchMe)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const logout = useAuthStore((s) => s.logout)
  const fetchRuntimeConfig = useFeatureFlagsStore((s) => s.fetchRuntimeConfig)
  const isExpired = useSessionStore((s) => s.isExpired)
  const resetSession = useSessionStore((s) => s.reset)
  const openLogin = useAuthModalStore((s) => s.open)

  // 모달은 인증돼 있었을 때만 표시. 익명 사용자가 401 받는 정상 케이스
  // (List 페이지 EmptyState forbidden) 와 구분.
  const showSessionExpired = isExpired && isAuthenticated

  // 앱 부팅 시 Cookie 기반 세션 복원 (GET /auth/me)
  useEffect(() => {
    fetchMe()
  }, [fetchMe])

  // 공개 runtime config 로 feature flag 복원. 실패 시 build-time fallback 유지.
  useEffect(() => {
    void fetchRuntimeConfig()
  }, [fetchRuntimeConfig])

  // SessionExpiredModal "다시 로그인" 핸들러: 세션 정리 + 로그인 모달 오픈.
  // 현재 path 를 returnTo 로 넘겨 로그인 후 동일 페이지 복귀.
  const handleRelogin = async () => {
    await logout()
    resetSession()
    openLogin('signin', window.location.pathname + window.location.search)
  }

  return (
    <div className="ppt-page">
      <div className="canvas">
        <Navbar />
        <Outlet />
      </div>
      <Footer />
      <LoginModal />
      <SessionExpiredModal open={showSessionExpired} variant="expired" onLogout={handleRelogin} />
    </div>
  )
}
